/**
 * Supplier-assignment upload dialog for the Call-Off Part Repository tab.
 *
 * Accepts a multi-sheet Excel (per-supplier sheets + LINES TO INACTIVE
 * sheets + ALL PART LIST) and reports counts of assignments created /
 * updated, parts deactivated, and default-supplier rows updated.
 */
import { CheckCircle, FileSpreadsheet, Loader2, Upload, XCircle } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import type { PartSupplierUploadResponse } from '../../../lib/api/types/parts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  uploadFile: File | null;
  onFileSelected: (file: File | null) => void;
  uploading: boolean;
  uploadResult: PartSupplierUploadResponse | null;
  onUpload: () => void;
  onDone: () => void;
}

export default function SupplierAssignmentUploadDialog({
  open,
  onOpenChange,
  uploadFile,
  onFileSelected,
  uploading,
  uploadResult,
  onUpload,
  onDone,
}: Props) {
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelected(e.dataTransfer.files[0]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Supplier Assignments</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!uploadResult ? (
            <>
              <div
                className="border-2 border-dashed rounded-lg p-8 text-center transition-colors border-muted-foreground/25"
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <FileSpreadsheet className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
                <p className="text-sm font-medium">
                  {uploadFile ? uploadFile.name : 'Drag & drop your Excel file here'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Supports .xlsx files
                </p>
                <label className="mt-3 inline-block">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) onFileSelected(e.target.files[0]);
                    }}
                  />
                  <Button variant="outline" size="sm" asChild>
                    <span>Browse Files</span>
                  </Button>
                </label>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                <p className="text-xs font-medium">Sheets processed:</p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">…&nbsp;STOCK&nbsp;LINES</span> — assigns parts to the supplier listed in the
                  &quot;Preferred Supplier&quot; column
                </p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">LINES&nbsp;TO&nbsp;INACTIVE&nbsp;…</span> — deactivates the matching part-supplier assignment
                </p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">ALL&nbsp;PART&nbsp;LIST</span> — updates default supplier on each part
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {uploadResult.error_count === 0 ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-amber-500" />
                )}
                <span className="font-medium">Upload Complete</span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/50 rounded p-2 text-center">
                  <div className="text-lg font-bold">{uploadResult.total_rows.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">Rows read</div>
                </div>
                <div className="bg-green-50 dark:bg-green-950/20 rounded p-2 text-center">
                  <div className="text-lg font-bold text-green-600">
                    {uploadResult.assignments_created.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Assignments created</div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/20 rounded p-2 text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {uploadResult.assignments_updated.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Assignments updated</div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/20 rounded p-2 text-center">
                  <div className="text-lg font-bold text-amber-600">
                    {uploadResult.assignments_deactivated.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Assignments deactivated</div>
                </div>
                <div className="bg-muted/50 rounded p-2 text-center col-span-2">
                  <div className="text-lg font-bold">
                    {uploadResult.default_suppliers_set.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground">Default suppliers set (ALL PART LIST)</div>
                </div>
              </div>

              {uploadResult.errors.length > 0 && (
                <div className="bg-red-50 dark:bg-red-950/20 rounded p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-medium text-red-600 mb-1">
                    {uploadResult.error_count} skipped row(s):
                  </p>
                  {uploadResult.errors.slice(0, 20).map((err, i) => (
                    <p key={i} className="text-xs text-red-500">
                      {err.sheet} · Row {err.row}: {err.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          {!uploadResult ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={onUpload} disabled={!uploadFile || uploading}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={onDone}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
